import React from 'react'
import { Tooltip } from 'antd'
import { CheckCircleFilled, ClockCircleOutlined, MinusCircleOutlined } from '@ant-design/icons'
import type { ColumnType } from 'antd/es/table'
import '../../styles/flagTooltip.css'

/**
 * Flaggans LÄGE (#30) — en egenskap hos flaggan just nu, inte en logg över vad som gjorts.
 *
 * Kolumnen svarar på en enda fråga: *kräver den här flaggan min uppmärksamhet?*
 * Vad som faktiskt gjorts, av vem och varför hör hemma i flagg-detaljvyns historik.
 *
 * Läget beräknas i backend (services/cases/flaggHantering.ts) så att regeln finns på
 * ett ställe. Frontend gör inget urval — den ritar bara det den får.
 */

export type FlaggLäge = 'ej_hanterad' | 'pågående' | 'hanterad'

export interface HanteringEntry {
  läge: FlaggLäge
  antal: number
}

interface HanteringBearer {
  hantering?: HanteringEntry
}

// Ikonerna har OLIKA FORM, inte bara olika färg. Enbart färg fungerar dåligt vid
// färgblindhet, och hover finns inte på pekskärm — formen måste bära informationen
// och färgen förstärka den. Pågående och Hanterad skiljs dessutom av kontur mot fylld,
// vilket läser som "på gång" mot "klar" även i gråskala.
//
// Medvetet INGET gult, orange eller rött: Risknivå-kolumnen använder den paletten, och
// en orange ikon bredvid en orange riskflagga läses som risk i stället för som status.
// #1890ff är appens blå, samma som länkarna i flaggvyerna.
const LÄGEN: Record<FlaggLäge, { text: string; icon: React.ReactNode }> = {
  ej_hanterad: {
    text: 'Ej hanterad',
    icon: <MinusCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.25)', fontSize: 16 }} />,
  },
  pågående: {
    text: 'Pågående',
    icon: <ClockCircleOutlined style={{ color: '#1890ff', fontSize: 16 }} />,
  },
  hanterad: {
    text: 'Hanterad',
    icon: <CheckCircleFilled style={{ color: '#1890ff', fontSize: 16 }} />,
  },
}

// Sorteringsordning: det som kräver uppmärksamhet först.
const RANG: Record<FlaggLäge, number> = { ej_hanterad: 0, pågående: 1, hanterad: 2 }

export const LÄGE_FILTER = [
  { value: 'ej_hanterad', label: 'Ej hanterad' },
  { value: 'pågående', label: 'Pågående' },
  { value: 'hanterad', label: 'Hanterad' },
]

export const hanteringColumn = <T extends HanteringBearer>(options?: { sortOrder?: 'ascend' | 'descend' | null }): ColumnType<T> => ({
  title: 'Status',
  dataIndex: 'hantering',
  key: 'hantering',
  // Fast till höger, precis innanför markeringskolumnen: status och kryssruta används
  // tillsammans (läs status -> bestäm -> kryssa), och tabellen scrollar i sidled med
  // nio kolumner. 85 px rymmer rubriken (6 tecken ~43 px) plus sorteringspil och padding.
  width: 90,
  fixed: 'right',
  showSorterTooltip: { overlayClassName: 'flag-tooltip', title: 'Flaggans status. Berättar om flaggan är hanterad eller inte' },
  ...(options && 'sortOrder' in options ? { sortOrder: options.sortOrder } : {}),
  render: (_, rad) => {
    const läge = rad.hantering?.läge ?? 'ej_hanterad'
    const { text, icon } = LÄGEN[läge]

    // Bara läget i tooltipen — antalet hanteringar är en detalj som hör hemma i
    // flagg-detaljvyn. Kolumnen är en triage-signal, inte en sammanfattning.
    //
    // Centrerat via cellinnehållet, inte via `align` — den senare centrerar även rubriken,
    // och rubrikerna ska vara vänsterjusterade som alla andra i tabellen.
    return (
      <div style={{ textAlign: 'center' }}>
        <Tooltip overlayClassName="flag-tooltip" title={text}>
          <span style={{ cursor: 'default' }}>{icon}</span>
        </Tooltip>
      </div>
    )
  },
  sorter: (a, b) => RANG[a.hantering?.läge ?? 'ej_hanterad'] - RANG[b.hantering?.läge ?? 'ej_hanterad'],
})
